import { jwtDecode } from 'jwt-decode';

// Token validation cache
const tokenValidationCache = new Map<string, { isValid: boolean; timestamp: number }>();
const CACHE_DURATION = 30 * 1000; // 30 saniye cache

// JWT Token'dan çıkarılan kullanıcı bilgileri interface'i
export interface JwtUser {
  nameid: string; // User ID
  unique_name: string; // Email
  email: string;
  given_name: string; // First Name
  family_name: string; // Last Name
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/mobilephone': string; // Phone
  FullName: string;
  IsEDevletVerified: string;
  UserId: string;
  role: string;
  nbf: number; // Not Before
  exp: number; // Expiration
  iat: number; // Issued At
  iss: string; // Issuer
  aud: string; // Audience
}

// JWT Token'dan kullanıcı bilgilerini çıkaran fonksiyon
export const decodeJwtToken = (token: string): JwtUser | null => {
  try {
    const decoded = jwtDecode<JwtUser>(token);
    return decoded;
  } catch (_error) {
    return null;
  }
};

// JWT Token'ın geçerli olup olmadığını kontrol eden fonksiyon
export const isTokenValid = (token: string): boolean => {
  try {
    // Token'ın varlığını ve formatını kontrol et
    if (!token || typeof token !== 'string') {
      return false;
    }
    
    // Cache kontrolü
    const now = Date.now();
    const cached = tokenValidationCache.get(token);
    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      return cached.isValid;
    }
    
    // JWT token 3 parçadan oluşmalı (header.payload.signature)
    const parts = token.split('.');
    if (parts.length !== 3) {
      tokenValidationCache.set(token, { isValid: false, timestamp: now });
      return false;
    }
    
    // Her parçanın boş olmadığını kontrol et
    if (parts.some(part => !part || part.trim() === '')) {
      tokenValidationCache.set(token, { isValid: false, timestamp: now });
      return false;
    }
    
    const decoded = jwtDecode<JwtUser>(token);
    const currentTime = Math.floor(Date.now() / 1000);
    
    // Token'ın süresi dolmuş mu kontrol et
    if (decoded.exp && decoded.exp < currentTime) {
      tokenValidationCache.set(token, { isValid: false, timestamp: now });
      return false;
    }
    
    // Token'ın henüz geçerli olmadığı zaman kontrol et
    if (decoded.nbf && decoded.nbf > currentTime) {
      tokenValidationCache.set(token, { isValid: false, timestamp: now });
      return false;
    }
    
    // Cache'e kaydet
    tokenValidationCache.set(token, { isValid: true, timestamp: now });
    return true;
  } catch (_error) {
    tokenValidationCache.set(token, { isValid: false, timestamp: Date.now() });
    return false;
  }
};

// JWT Token'dan kullanıcı bilgilerini çıkarıp User interface'ine dönüştüren fonksiyon
export const extractUserFromToken = (token: string) => {
  const jwtUser = decodeJwtToken(token);
  
  if (!jwtUser) {
    return null;
  }
  
  return {
    id: jwtUser.nameid || jwtUser.UserId,
    userName: jwtUser.unique_name,
    email: jwtUser.email,
    firstName: jwtUser.given_name,
    lastName: jwtUser.family_name,
    fullName: jwtUser.FullName,
    phone: jwtUser['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/mobilephone'],
    isEDevletVerified: jwtUser.IsEDevletVerified === 'True',
    roles: [jwtUser.role]
  };
};

// Token'ın ne kadar süre sonra dolacağını hesaplayan fonksiyon
export const getTokenExpirationTime = (token: string): number | null => {
  try {
    const decoded = jwtDecode<JwtUser>(token);
    return decoded.exp ? decoded.exp * 1000 : null; // Convert to milliseconds
  } catch (_error) {
    return null;
  }
};

// Token'ın ne kadar süre kaldığını hesaplayan fonksiyon (milisaniye cinsinden)
export const getTokenTimeRemaining = (token: string): number | null => {
  const expirationTime = getTokenExpirationTime(token);
  
  if (!expirationTime) {
    return null;
  }
  
  const currentTime = Date.now();
  const timeRemaining = expirationTime - currentTime;
  
  return timeRemaining > 0 ? timeRemaining : 0;
};

// Token'ın refresh edilmesi gerekip gerekmediğini kontrol eden fonksiyon
export const shouldRefreshToken = (token: string): boolean => {
  const timeRemaining = getTokenTimeRemaining(token);
  const bufferTime = 5 * 60 * 1000; // 5 dakika buffer
  return timeRemaining !== null && timeRemaining < bufferTime;
};

// Token'ın yakında dolup dolmayacağını kontrol eden fonksiyon (10 dakika)
export const isTokenExpiringSoon = (token: string): boolean => {
  const timeRemaining = getTokenTimeRemaining(token);
  const warningTime = 10 * 60 * 1000; // 10 dakika
  return timeRemaining !== null && timeRemaining < warningTime;
};

// Token'ın formatını kontrol eden fonksiyon
export const isValidTokenFormat = (token: string): boolean => {
  if (!token || typeof token !== 'string') {
    return false;
  }
  
  const parts = token.split('.');
  if (parts.length !== 3) {
    return false;
  }
  
  return parts.every(part => part && part.trim() !== '');
};

// Refresh token'ın formatını kontrol eden fonksiyon (Base64 encoded string)
export const isValidRefreshTokenFormat = (token: string): boolean => {
  if (!token || typeof token !== 'string') {
    return false;
  }
  
  // Base64 encoded string formatında olmalı
  return token.length > 10 && token.trim() !== '';
};

// Token validation cache'ini temizle
export const clearTokenValidationCache = (): void => {
  tokenValidationCache.clear();
};

// Belirli bir token'ın cache'ini temizle
export const clearTokenFromCache = (token: string): void => {
  tokenValidationCache.delete(token);
};

// Refresh token'ın geçerli olup olmadığını kontrol eden fonksiyon
export const isRefreshTokenValid = (token: string): boolean => {
  try {
    // Token'ın varlığını ve formatını kontrol et
    if (!token || typeof token !== 'string') {
      return false;
    }
    
    // Base64 encoded string formatında olmalı
    return token.length > 10 && token.trim() !== '';
  } catch (_error) {
    return false;
  }
};
